import {useParams} from "react-router-dom";
import {useSearchContext} from "v4/contexts/SearchContext/SearchContext";
import useFetch from "v4/hooks/useFetch";
import {useCallback, useEffect, useMemo, useState} from "react";
import {generateUrl} from "v4/services/api.service";
import useAutoFetch from "v4/hooks/useAutoFetch";
import {useForm, useWatch} from "react-hook-form";
import jsonToFormData from "v4/utils/jsonToFormData";
import {useImportContext} from "v4/pages/admin/Import/contexts/ImportContext";
import useNotification from "v4/hooks/useNotification";
import {downloadBlob} from "v4/utils";
import {useTranslation} from "react-i18next";
import {sendBrowserNotification} from "v4/components/utils/NotifyMe/utils/sendBrowserNotification";
import usePrevious from "v4/hooks/usePrevious";

export default function useImportTable(dataRouteName, searchRouteName) {
    const {t} = useTranslation();
    const [searchFormFields, setSearchFormFields] = useState([]);

    const {id: importId} = useParams();
    const {
        initPagesWithTotalItems,
        currentPage,
        pages,
    } = useSearchContext();
    const {updateSelectedEntityType} = useImportContext();
    const methods = useForm();
    const {register, setValue, getValues, control} = methods;
    const {data: searchFormData} = useAutoFetch(generateUrl(searchRouteName));
    const [{data, isLoading}, fetchTable] = useFetch();
    const [{data: exportFile}, downloadTable] = useFetch('blob');
    const {addInfoNotification} = useNotification();
    const [tableHeaders, tableRows, fullData] = useMemo(() => {
        const rows = data?.data ?? [];
        const headers = data?.headers ? Object.entries(data.headers).map(([key, label]) => ({key, label})) : [];
        return [headers, rows, data];
    }, [data]);

    const previousTotalFilteredItems = usePrevious(data?.totalFilteredItems);
    useEffect(() => {
        if ((pages || !data?.totalFilteredItems) && previousTotalFilteredItems === data?.totalFilteredItems) return;

        initPagesWithTotalItems(data.totalFilteredItems, data?.itemsPerPage ?? 50);

        if (!data.entity) return;

        updateSelectedEntityType(data.entity);
    }, [data, initPagesWithTotalItems, pages, previousTotalFilteredItems, updateSelectedEntityType]);

    const getSortableFieldName = useCallback((name) => {
        return searchFormFields.find(field => field.name.includes(name))?.name;
    }, [searchFormFields]);

    useEffect(() => {
        const pageFieldName = getSortableFieldName('page');

        if (pageFieldName) setValue(pageFieldName, currentPage);
    }, [currentPage, getSortableFieldName, searchFormFields, setValue]);

    const onSort = (label, sortOrder) => {
        const sortFieldName = getSortableFieldName('sortField')
        const sortOrderName = getSortableFieldName('sortOrder')

        if (sortFieldName && label) setValue(sortFieldName, label);
        if (sortOrderName && sortOrder) setValue(sortOrderName, sortOrder.toUpperCase());
    }

    const onRowsStatusSort = (rowsStatus) => {
        const sortRowsStatusName = getSortableFieldName('rowsStatus');

        if (sortRowsStatusName && rowsStatus) {
            const currentStatus = getValues(sortRowsStatusName);

            // Allow to toggle the status filter
            setValue(sortRowsStatusName, currentStatus === rowsStatus ? '' : rowsStatus);
        }
    }

    const onExportDownload = () => {
        const isExportName = getSortableFieldName('isExport');
        const sortRowsStatusName = getSortableFieldName('rowsStatus');

        if (isExportName && sortRowsStatusName) {
            const formData = jsonToFormData(getValues());
            formData.set(isExportName, true);

            downloadTable({
                url: generateUrl(dataRouteName, {importId}),
                config: {
                    method: 'POST',
                    body: formData,
                }
            });
            addInfoNotification(t('fetching_file'));
        }
    }

    useEffect(() => {
        if (!exportFile) return;

        sendBrowserNotification(t('export_completed'), { tag: 'pol-export' });
        downloadBlob(exportFile, exportFile.name ?? 'export.csv');
    }, [exportFile, t]);

    const isFormReady = useMemo(() => {
        if (!searchFormData?.['hydra:member']?.results) return false;

        Object.entries(searchFormData['hydra:member'].results).forEach(([fieldName, field]) => {
            register(fieldName, {value: field.value});
            setSearchFormFields(prev => {
                if (prev.find(f => f.name === fieldName)) return prev;
                return [...prev, field];
            });
        });

        return true;
    }, [register, searchFormData]);

    const searchValues = useWatch({control});

    useEffect(() => {
        if (!isFormReady || !Object.keys(searchValues ?? {}).length) return;

        fetchTable({
            url: generateUrl(dataRouteName, {importId}),
            config: {
                method: 'POST',
                body: jsonToFormData(searchValues)
            }
        })
    }, [fetchTable, importId, dataRouteName, isFormReady, searchValues]);

    const getSortableFieldValues = useCallback((name) => {
        const fieldName = getSortableFieldName(name);
        if (!fieldName) return null;

        return getValues(fieldName);
    }, [getSortableFieldName, getValues]);

    return {
        isTableLoading: isLoading,
        tableHeaders,
        tableRows,
        fullData,
        methods,
        searchFormData,
        onSort,
        onRowsStatusSort,
        onExportDownload,
        getSortableFieldName,
        getSortableFieldValues,
    }
}
